<template>
	<div class="container">
		<h2 class="heading-2 global-box-heading">{{ $t('shopping') }}</h2>
		<div
			v-for="(shopping, index) in shoppingData.cmsExpenseSubCategories"
			:key="index"
			class="container-shopping"
		>
			<ja-input
				v-model.trim="shopping.tab.name"
				:disabled="!canEdit"
				:label="$t('tab')"
				:name="$t('tab')"
				@blur="handleBlur"
			></ja-input>
			<div>
				<h2 class="heading-5">{{ $t('image') }}</h2>
				<div class="select-file">
					<ja-button
						name="selectFile"
						class="small outlined"
						@click.native="openFileSelector(index)"
						>{{ $t('selectFile') }}</ja-button
					>
					<img
						v-show="shopping.imageBase64"
						ref="imageLogo"
						:src="shopping.imageBase64"
						:alt="'logo' + shopping.name"
						class="thumbnail"
					/>
					<span v-show="!shopping.imageBase64">{{ $t('chooseALogo') }}</span>
					<input
						ref="fileInput"
						type="file"
						style="display: none;"
						accept=".png, .jpg, .jpeg"
						@change="validateFile($event, index)"
					/>
				</div>
				<div v-show="fileInfo" class="select-file  selection">
					<div class="file-info">
						<span v-if="!fileValid" class="text--warning">{{
							$t('formatPNG')
						}}</span>
					</div>
				</div>
			</div>

			<ja-input
				v-model.trim="shopping.name"
				:value="shopping.name"
				:label="$t('title')"
				:name="$t('title')"
				@blur="handleBlur"
			></ja-input>
			<ja-textarea
				v-model="shopping.description"
				:max-length="textAreaMaxLength"
				is-legend
				:label="$t('description')"
				:name="$t('description')"
				@blur="handleBlur"
			></ja-textarea>
			<ja-input
				v-model.trim="shopping.cost"
				:value="shopping.cost"
				is-number-input
				:label="$t('cost')"
				:name="$t('cost')"
				@blur="handleBlur"
			></ja-input>
			<ja-input
				v-model.trim="shopping.costPerChild"
				:value="shopping.costPerChild"
				is-number-input
				:label="$t('costPerChildCMS')"
				:name="$t('costPerChildCMS')"
				@blur="handleBlur"
			></ja-input>
			<ja-dropdown
				v-model="hasQuantityIndex[index]"
				:label="$t('hasQuantity')"
				:options="yesOrNoOptions"
				name="hasQuantity"
				@input="hasQuantitySelected(index)"
				@blur="handleBlur"
			></ja-dropdown>
			<ja-dropdown
				v-model="showMonthlyCostIndex[index]"
				:label="$t('showMonthlyCost')"
				:options="yesOrNoOptions"
				name="showMonthlyCost"
				@input="showMonthlyCostSelected(index)"
				@blur="handleBlur"
			></ja-dropdown>
			<div>
				<h2 class="heading-5">{{ $t('availableIn') }}</h2>
				<div class="container-input-checkbox">
					<div
						v-for="(option, i) in shopping.lifeStages"
						:key="i"
						class="option"
					>
						<input
							ref="checkboxSimulationType"
							v-model="option.isActive"
							:name="option.name"
							type="checkbox"
							class="checkbox"
							@change="simulationTypeCheck(option, index)"
							@blur="handleBlur"
						/>
						<label :for="option.name" class="body-text-2 input-label">
							{{ option.name }}
						</label>
					</div>
				</div>
			</div>

			<ja-dropdown
				v-model="isMandatoryIndex[index]"
				:label="$t('isMandatory')"
				:options="yesOrNoOptions"
				name="isMandatory"
				@input="isMandatorySelected(index)"
				@blur="handleBlur"
			></ja-dropdown>
			<ja-button
				:disabled="shopping.length === 0"
				name="cancel"
				class="small outlined button-add delete"
				@click.native="deleteExpenseSubCategories(index)"
				>{{ $t('delete') }}</ja-button
			>
		</div>
		<div class="container-buttons">
			<ja-button
				name="save"
				class="small outlined button-add"
				@click.native="addExpenseSubCategory"
				>{{ $t('addAnother') }}</ja-button
			>
		</div>
	</div>
</template>
<script>
import { Input, Dropdown, Textarea } from '@/components/ui/form';
import Button from '@/components/ui/Button';
import UserData from '@/mixins/UserData';
import { isActive, cmsTextAreaMaxLength } from '@/helpers/constants';
import { ResizeImage } from '@/mixins';
import '@/components/icons/cms/search';
import '@/components/icons/cms/pencil';
import '@/components/icons/cms/trash';

export default {
	components: {
		jaDropdown: Dropdown,
		jaTextarea: Textarea,
		jaInput: Input,
		jaButton: Button
	},
	mixins: [UserData, ResizeImage],
	props: {
		data: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			shoppingData: null,
			categoryTabsData: null,
			lifeStageData: [],
			isMandatoryIndex: [],
			showMonthlyCostIndex: [],
			hasQuantityIndex: [],
			yesOrNoOptions: [],
			fileValid: false,
			fileInfo: null,
			textAreaMaxLength: cmsTextAreaMaxLength.SHOPPING_FORM
		};
	},
	computed: {
		canEdit() {
			return this.isAdmin;
		}
	},
	created() {
		this.yesOrNoOptions = isActive;
		this.shoppingData = this.data;
		this.categoryTabsData = this.data.cmsExpenseCategoryTabs;
		this.setInitialValues();
	},
	methods: {
		setInitialValues() {
			this.shoppingData.cmsExpenseSubCategories.forEach(data => {
				data.lifeStages = [];
				this.data.lifeStages.forEach(ls => {
					data.lifeStages.push({ ...ls });
				});

				const isMandatoryIdx = this.yesOrNoOptions.findIndex(
					option => option.value === data.isMandatory
				);

				const showMonthlyCostIdx = this.yesOrNoOptions.findIndex(
					option => option.value === data.showMontlyCost
				);

				const hasQuantityIdx = this.yesOrNoOptions.findIndex(
					option => option.value === data.canChooseQuantity
				);

				this.isMandatoryIndex.push(isMandatoryIdx);
				this.showMonthlyCostIndex.push(showMonthlyCostIdx);
				this.hasQuantityIndex.push(hasQuantityIdx);

				data.lifeStages.forEach(stage => {
					stage.isActive = data.selectedLifeStages.includes(stage.lifeStage_ID);
				});
			});
		},
		isMandatorySelected(index) {
			const isMandatoryIndex = this.isMandatoryIndex[index];
			this.shoppingData.cmsExpenseSubCategories[
				index
			].isMandatory = this.yesOrNoOptions[isMandatoryIndex].value;
		},
		showMonthlyCostSelected(index) {
			const showMonthlyIndex = this.showMonthlyCostIndex[index];
			this.shoppingData.cmsExpenseSubCategories[
				index
			].showMontlyCost = this.yesOrNoOptions[showMonthlyIndex].value;
		},
		hasQuantitySelected(index) {
			const hasQuantityIndex = this.hasQuantity[index];
			this.shoppingData.cmsExpenseSubCategories[
				index
			].canChooseQuantity = this.yesOrNoOptions[hasQuantityIndex].value;
		},
		simulationTypeCheck(option, index) {
			if (option.isActive) {
				if (
					!this.shoppingData.cmsExpenseSubCategories[
						index
					].selectedLifeStages.includes(option.lifeStage_ID)
				) {
					this.shoppingData.cmsExpenseSubCategories[
						index
					].selectedLifeStages.push(option.lifeStage_ID);
				}
			} else {
				const indexToRemove = this.shoppingData.cmsExpenseSubCategories[
					index
				].selectedLifeStages.indexOf(option.lifeStage_ID);
				if (indexToRemove !== -1) {
					this.shoppingData.cmsExpenseSubCategories[
						index
					].selectedLifeStages.splice(indexToRemove, 1);
				}
			}
		},
		getTabName(subcategory) {
			if (subcategory.tab) {
				return subcategory.tab.name;
			}
			return '';
		},
		async readFileAsync(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					resolve(reader.result);
				};
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		},
		openFileSelector(index) {
			const fileInput = this.$refs.fileInput[index];
			if (fileInput) {
				fileInput.click();
			}
		},
		async imageSize(file) {
			const maxLogoWidth = 250;
			const maxLogoHeight = 250;
			let fileDataURL = file;
			const resizedFile = await this.resizeImage(
				file,
				maxLogoWidth,
				maxLogoHeight
			);
			fileDataURL = await this.readFileAsync(resizedFile);

			return fileDataURL;
		},
		async validateFile(event, index) {
			const input = event.target;
			const file = input.files[0];

			const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
			this.fileInfo = file.name;
			if (!allowedTypes.includes(file.type)) {
				this.fileValid = false;
				return;
			}

			const resizedDataURL = await this.imageSize(file);
			this.shoppingData.cmsExpenseSubCategories[
				index
			].imageBase64 = resizedDataURL;
			this.handleBlur();
			this.fileValid = true;
		},
		deleteExpenseSubCategories(index) {
			this.shoppingData.cmsExpenseSubCategories.splice(index, 1);
		},
		addExpenseSubCategory() {
			const researchBase = this.shoppingData.cmsExpenseSubCategoryEmpty;
			researchBase.expenseCategory_ID = this.shoppingData.expenseCategory_ID;
			researchBase.expenseCategoryTab_ID =
				this.shoppingData.cmsExpenseCategoryTabs != null &&
				this.shoppingData.cmsExpenseCategoryTabs.length
					? this.shoppingData.cmsExpenseCategoryTabs[0].expenseCategoryTabs_ID
					: null;
			researchBase.tab = {};
			researchBase.tab.name = '';
			researchBase.lifeStages = this.data.lifeStages;
			researchBase.lifeStages.forEach(stage => {
				stage.isActive = researchBase.selectedLifeStages.includes(
					stage.lifeStage_ID
				);
			});
			this.setInitialValues();
			this.shoppingData.cmsExpenseSubCategories.push(researchBase);
		},
		handleBlur() {
			this.$emit('shoppingData', this.shoppingData);
		}
	}
};
</script>
<style lang="scss" scoped>
.container {
	margin-top: 60px;
}
.container-shopping {
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin-top: 40px;
}
.container-buttons {
	display: flex;
	align-items: flex-start;
	gap: 40px;
	justify-content: center;
	left: -70px;
	position: relative;
}
.container-input-checkbox {
	border: 1px solid $button-border;
	display: flex;
	justify-content: flex-start;
	padding: 12px;
	margin-bottom: 25px;
	gap: 50px;
}
.option {
	display: flex;
	align-items: center;
	gap: 10px;
}
.file-info {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.input-label {
	cursor: pointer;
}
.select-file {
	display: flex;
	align-items: center;
	gap: 30px;
	margin-bottom: 25px;
}
.selection {
	align-items: center;
}
.heading-5 {
	color: $font-dark;
	margin: 10px 0;
}
.thumbnail {
	width: 90px;
	height: auto;
}
.button-add {
	min-width: 160px !important;
}
.delete {
	width: 150px;
}
</style>
